// Cover and share images are re-encoded in the browser before they reach
// Storage: same pixels, far fewer bytes. Share images stay JPEG because some
// apps (and WhatsApp's size limit) don't get along with WebP or heavy files.
export const COVER_IMAGE_FORMAT = { type: 'image/webp', quality: 0.8, extension: 'webp' };
export const SOCIAL_IMAGE_FORMAT = { type: 'image/jpeg', quality: 0.85, extension: 'jpg' };

const COMPRESSIBLE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MIN_SAVING = 0.15;
const NIGHT = '#0F172A';

export const isWorthCompressing = (type) => COMPRESSIBLE_TYPES.includes(String(type || '').toLowerCase());

// Re-encoding an already light file can lose quality for almost nothing.
export const shouldUseCompressed = ({ originalBytes, compressedBytes, requestedType, compressedType }) => (
  compressedType === requestedType && compressedBytes <= originalBytes * (1 - MIN_SAVING)
);

const loadImage = (file) => new Promise((resolve, reject) => {
  const objectUrl = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error('Unable to read the image.'));
  };
  image.src = objectUrl;
});

// Any problem hands back the original file: a heavy image is better than a
// failed save.
export const compressImageFile = async (file, format = COVER_IMAGE_FORMAT) => {
  if (!isWorthCompressing(file?.type)) return file;
  try {
    const image = await loadImage(file);
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext('2d');
    if (format.type === 'image/jpeg') {
      // JPEG has no transparency; without this it would turn black.
      context.fillStyle = NIGHT;
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    context.drawImage(image, 0, 0);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, format.type, format.quality));
    if (!blob || !shouldUseCompressed({
      originalBytes: file.size,
      compressedBytes: blob.size,
      requestedType: format.type,
      compressedType: blob.type,
    })) return file;
    const baseName = String(file.name || 'image').replace(/\.[^/.]+$/, '');
    return new File([blob], `${baseName}.${format.extension}`, { type: blob.type });
  } catch (error) {
    return file;
  }
};
